import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { BookableViewingContact, BookableViewingEvent } from '@app/events/shared/event.service';
import { EventRoleName } from '@app/events/shared/event-role-name.model';
import { EventRoleNameService } from '@app/events/shared/event-role-name.service';
import { LucideAngularModule, ArrowLeft, Calendar, CheckCircle, Search, X } from 'lucide-angular';

/**
 * TVG-331: "Book New Viewing" — book a viewing for any venue, from the Deals
 * board, without first finding the card.
 *
 * Until now a viewing could only be booked by dragging an existing card into
 * the Viewing Booked column (TVG-134), so the event had to already be in front
 * of the sales manager. This dialog starts from the contact instead: search the
 * contacts that have a bookable event, pick the contact, pick which of their
 * events the viewing is for (each row names its venue, so any venue is
 * reachable), then set the date. Time stays optional, matching TVG-134.
 *
 * The dialog only chooses what to book; the board books it through the same
 * endpoint the drag-and-drop dialog uses, so the show round is saved on the
 * event in exactly one way and lists on the contact record (TVG-324) unchanged.
 */
export interface BookNewViewingPayload {
  event_id: number;
  show_round_date: string;
  show_round_time: string | null;
  /** TVG-379: who will show them round, recorded on this viewing. */
  show_around_name_id: number | null;
}

type Step = 'contact' | 'event' | 'date';

@Component({
  selector: 'app-book-new-viewing-modal',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule, ButtonComponent, LucideAngularModule],
  template: `
    @if (isOpen) {
      <div class="fixed inset-0 z-[1000] overflow-y-auto" data-testid="book-new-viewing-modal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/50 transition-opacity" (click)="onClose()"></div>

          <div class="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
            <div class="flex items-start gap-4 p-6 pb-4">
              <div class="flex h-11 w-11 items-center justify-center rounded-full bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300">
                <lucide-icon [name]="CalendarIcon" [size]="22"></lucide-icon>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Book new viewing</h3>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  @switch (step) {
                    @case ('contact') {
                      Find the contact the viewing is for.
                    }
                    @case ('event') {
                      Pick which of {{ selectedContact?.name }}'s events the viewing is for.
                    }
                    @default {
                      Set the viewing date for {{ selectedEvent?.venue?.name ?? 'this event' }}.
                    }
                  }
                </p>
              </div>
              <button
                class="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                (click)="onClose()"
                aria-label="Close book new viewing modal">
                <lucide-icon [name]="XIcon" [size]="18"></lucide-icon>
              </button>
            </div>

            @switch (step) {
              @case ('contact') {
                <div class="px-6 pb-2">
                  <label class="sr-only" for="book-new-viewing-search">Search contacts</label>
                  <div class="relative">
                    <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <lucide-icon [name]="SearchIcon" [size]="16"></lucide-icon>
                    </span>
                    <input
                      id="book-new-viewing-search"
                      type="search"
                      [formControl]="searchControl"
                      placeholder="Search by name or email"
                      data-testid="book-new-viewing-search"
                      class="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                  </div>

                  <div class="mt-3 max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    @if (loading) {
                      <div class="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
                    } @else {
                      @for (contact of contacts; track contact.id) {
                        <button
                          type="button"
                          class="flex w-full flex-col items-start gap-0.5 border-b border-gray-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/5"
                          data-testid="book-new-viewing-contact"
                          (click)="selectContact(contact)">
                          <span class="text-sm font-medium text-gray-800 dark:text-white/90">{{ contact.name }}</span>
                          <span class="text-xs text-gray-500 dark:text-gray-400">
                            {{ contact.email }} · {{ contact.events.length }}
                            {{ contact.events.length === 1 ? 'event' : 'events' }}
                          </span>
                        </button>
                      } @empty {
                        <div class="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                          No contacts with an event a viewing can be booked on.
                        </div>
                      }
                    }
                  </div>
                </div>
              }

              @case ('event') {
                <div class="px-6 pb-2">
                  <div class="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    @for (event of selectedContact?.events ?? []; track event.id) {
                      <button
                        type="button"
                        class="flex w-full flex-col items-start gap-0.5 border-b border-gray-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/5"
                        data-testid="book-new-viewing-event"
                        (click)="selectEvent(event)">
                        <span class="text-sm font-medium text-gray-800 dark:text-white/90">
                          {{ event.name || event.reference || 'Event #' + event.id }}
                        </span>
                        <span class="text-xs text-gray-500 dark:text-gray-400">
                          {{ event.venue?.name ?? 'No venue' }}
                          @if (event.start_date) {
                            · {{ event.start_date | date: 'd MMM y' }}
                          }
                          · {{ event.status }}
                        </span>
                        @if (event.show_round) {
                          <span class="text-xs text-amber-600 dark:text-amber-400">
                            Viewing already booked for {{ event.show_round | date: 'd MMM y' }} — booking again adds a second viewing.
                          </span>
                        }
                      </button>
                    } @empty {
                      <div class="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        This contact has no event a viewing can be booked on.
                      </div>
                    }
                  </div>
                </div>
              }

              @default {
                <form [formGroup]="form" class="space-y-3 px-6 pb-2">
                  <div class="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-gray-800/60 dark:text-gray-300">
                    <span class="font-medium text-gray-800 dark:text-white/90">{{ selectedContact?.name }}</span>
                    —
                    {{ selectedEvent?.name || selectedEvent?.reference || 'Event #' + selectedEvent?.id }}
                    @if (selectedEvent?.venue) {
                      at {{ selectedEvent?.venue?.name }}
                    }
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="book-new-viewing-date">
                      Viewing date
                    </label>
                    <input
                      id="book-new-viewing-date"
                      type="date"
                      formControlName="date"
                      data-testid="book-new-viewing-date"
                      class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="book-new-viewing-time">
                      Time (optional)
                    </label>
                    <input
                      id="book-new-viewing-time"
                      type="time"
                      formControlName="time"
                      data-testid="book-new-viewing-time"
                      class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                  </div>
                  <!-- TVG-379: who will show them round, kept on this viewing. -->
                  @if (showAroundNames.length > 0) {
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="book-new-viewing-show-around">
                        Show around (optional)
                      </label>
                      <select
                        id="book-new-viewing-show-around"
                        formControlName="showAroundNameId"
                        data-testid="book-new-viewing-show-around"
                        class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                        <option [ngValue]="null">—</option>
                        @for (name of showAroundNames; track name.id) {
                          <option [ngValue]="name.id">{{ name.name }}</option>
                        }
                      </select>
                    </div>
                  }
                </form>
              }
            }

            <div class="mt-4 flex items-center justify-between gap-3 bg-gray-50 px-6 py-4 dark:bg-gray-800/60">
              <div>
                @if (step !== 'contact') {
                  <app-button
                    [label]="'Back'"
                    [style]="'secondary'"
                    [icon]="BackIcon"
                    [disabled]="submitting"
                    data-testid="book-new-viewing-back"
                    (click)="goBack()">
                  </app-button>
                }
              </div>
              <div class="flex items-center gap-3">
                <app-button
                  [label]="'Cancel'"
                  [style]="'secondary'"
                  [icon]="XIcon"
                  [disabled]="submitting"
                  data-testid="book-new-viewing-cancel"
                  (click)="onClose()">
                </app-button>
                @if (step === 'date') {
                  <app-button
                    [label]="submitting ? 'Booking…' : 'Book viewing'"
                    [style]="'primary'"
                    [icon]="CheckIcon"
                    [disabled]="submitting || form.invalid"
                    data-testid="book-new-viewing-submit"
                    (click)="onConfirm()">
                  </app-button>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class BookNewViewingModalComponent {
  @Input() isOpen = false;
  @Input() submitting = false;
  /** Contacts with a bookable event, loaded by the board for the current search. */
  @Input() contacts: BookableViewingContact[] = [];
  @Input() loading = false;

  /** Emits the search term as the operator types, so the board refetches. */
  @Output() searchChanged = new EventEmitter<string>();
  /** Emits the chosen event plus date/time so the board books the viewing. */
  @Output() confirmed = new EventEmitter<BookNewViewingPayload>();
  /** Dismissed without booking — no state change. */
  @Output() cancelled = new EventEmitter<void>();

  protected step: Step = 'contact';
  protected selectedContact: BookableViewingContact | null = null;
  protected selectedEvent: BookableViewingEvent | null = null;

  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });

  protected readonly form = new FormGroup({
    date: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    time: new FormControl<string>('', { nonNullable: true }),
    showAroundNameId: new FormControl<number | null>(null),
  });

  /** TVG-379: the active Show around names; empty (and the field hidden) if the list is unavailable. */
  protected showAroundNames: EventRoleName[] = [];

  protected readonly CalendarIcon = Calendar;
  protected readonly XIcon = X;
  protected readonly CheckIcon = CheckCircle;
  protected readonly SearchIcon = Search;
  protected readonly BackIcon = ArrowLeft;

  constructor(eventRoleNameService: EventRoleNameService) {
    eventRoleNameService.getByRole('show_around').subscribe({
      next: (response) => (this.showAroundNames = response.data ?? []),
      error: () => (this.showAroundNames = []),
    });

    // The contact list is small and server-filtered; emitting per keystroke
    // keeps the dialog dumb and lets the board own debouncing.
    this.searchControl.valueChanges.subscribe((term) => this.searchChanged.emit(term));
  }

  protected selectContact(contact: BookableViewingContact): void {
    this.selectedContact = contact;
    // A contact with exactly one bookable event has nothing to choose between,
    // so skip straight to the date rather than showing a one-row list.
    if (contact.events.length === 1) {
      this.selectEvent(contact.events[0]);
      return;
    }
    this.step = 'event';
  }

  protected selectEvent(event: BookableViewingEvent): void {
    this.selectedEvent = event;
    this.step = 'date';
  }

  protected goBack(): void {
    if (this.step === 'date') {
      this.selectedEvent = null;
      // Mirror selectContact(): a single-event contact has no event step to
      // return to, so Back from the date lands on the contact search.
      this.step = (this.selectedContact?.events.length ?? 0) > 1 ? 'event' : 'contact';
      if (this.step === 'contact') {
        this.selectedContact = null;
      }
      return;
    }
    this.selectedContact = null;
    this.step = 'contact';
  }

  protected onConfirm(): void {
    if (this.submitting || this.form.invalid || this.selectedEvent === null) {
      return;
    }
    this.confirmed.emit({
      event_id: this.selectedEvent.id,
      show_round_date: this.form.controls.date.value,
      show_round_time: this.form.controls.time.value || null,
      show_around_name_id: this.form.controls.showAroundNameId.value,
    });
  }

  protected onClose(): void {
    this.cancelled.emit();
  }

  /**
   * TVG-360: open already knowing who — and sometimes which event — the viewing
   * is for. The contact page has both in front of it, so staff should not have
   * to search for a contact they are already looking at. The board opens the
   * dialog the same way it always did; this is an extra way in, not a change to
   * it.
   */
  startWithContact(contact: BookableViewingContact, eventId: number | null = null): void {
    this.reset();
    this.selectedContact = contact;

    const event = eventId !== null ? contact.events.find((candidate) => candidate.id === eventId) : undefined;
    if (event) {
      this.selectEvent(event);
      return;
    }

    // Same rule as picking a contact out of the search: one bookable event is
    // nothing to choose between, so go straight to the date.
    if (contact.events.length === 1) {
      this.selectEvent(contact.events[0]);
      return;
    }

    this.step = 'event';
  }

  reset(): void {
    this.step = 'contact';
    this.selectedContact = null;
    this.selectedEvent = null;
    this.form.reset({ date: '', time: '', showAroundNameId: null });
    this.searchControl.setValue('', { emitEvent: false });
  }
}
